import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';

import InvoiceCategoryTabs from '../../../components/Billing/Invoice/InvoiceCategoryTabs';
import SearchBar from '../../../components/Billing/Invoice/InvoiceSearchBar';
import InvoiceFilter, { InvoiceFilterValues } from '../../../components/Billing/Invoice/InvoiceFilter';
import { ReactComponent as FunnelIcon } from '../../../assets/SVG/Filter Icon Box.svg';
import CheckedInList from './CheckedIn_Advance/CheckedInList';
import AdvanceRequestList from './Advance_Request/AdvanceRequestList';
import ReceivedList from './Recieved_Advance/ReceivedList';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppQuery } from '../../../services/appQuery';
import { AdvancesStats, getAdvancesStatsURL } from './advance.helper';
import { getRequest } from '../../../services/httpService';

type AdvanceTabKey = 'Checked In' | 'Package Advance' | 'Received';

const Advance: React.FC = () => {
  // Header states
  const [activeTab, setActiveTab] = useState<AdvanceTabKey>('Checked In');
  const [search, setSearch] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<InvoiceFilterValues>({ fromDate: '', toDate: '', billType: '' });
  const location = useLocation();
  const navigate = useNavigate();


  const { data: AdvancesStats } = useAppQuery<AdvancesStats>(
    ['AdvancesStats'],
    () => getRequest<AdvancesStats>(getAdvancesStatsURL()),
    { staleTime: 0, gcTime: 0, enabled: true, refetchOnMount: true }
  );
    // Category tabs with counts (wire to data later)
  const { t : tBilling } = useTranslation('billing_config');
  const categories = useMemo(() => ([
    { name: 'Checked In', label: tBilling('Advance.checked_in', 'Checked In'), count: AdvancesStats?.checked_in_today },
    // { name: 'Package Advance', label: tBilling('Advance.package_advance', 'Package Advance'), count: AdvancesStats?.pending_advance_requests },
    { name: 'Received', label: tBilling('Advance.advance_log', 'Advance Log'), count: AdvancesStats?.total_credit_history },
  ]), [AdvancesStats, tBilling]);
  
  // Sync active tab with URL query parameters
  useEffect(() => { 
    const params = new URLSearchParams(location.search);
    if (params.has('checked-in')) {
      setActiveTab('Checked In');
    } else if (params.has('advance-request')) {
      setActiveTab('Package Advance');
    } else if (params.has('received')) {
      setActiveTab('Received');
    }
  }, [location.search]);



  const renderTabContent = () => {
    switch (activeTab) {
    case 'Checked In':
      return <CheckedInList search={search} />;
    case 'Package Advance':
      return <AdvanceRequestList search={search} filters={filters} />;
    case 'Received':
      return <ReceivedList search={search} filters={filters} />;
    default:
      return null;
    }
  };

  return (
    <Box data-testid="advance-page-container">
      {/* Header: Tabs left, filter + search right */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }} data-testid="advance-header-section">
        <InvoiceCategoryTabs
          activeCategory={activeTab}
          categories={categories}
          onCategoryChange={(cat) => {
            setActiveTab(cat.name as AdvanceTabKey);
            if (location.search) {
              navigate(location.pathname, { replace: true });
            }
          }}
        />

        <Stack direction="row" spacing={1.5} alignItems="center">
          {activeTab !== 'Checked In' && (
            <Tooltip title={tBilling('Advance.filters', { defaultValue: 'Filters' })} arrow>
              <IconButton aria-label={tBilling('Advance.aria.open_filters', { defaultValue: 'Open filters' })} onClick={() => setIsFilterOpen(true)} sx={{ width: 40, height: 40, p: 0 }}>
                <FunnelIcon style={{ width: 40, height: 40 }}   />
              </IconButton>
            </Tooltip>
          )}
          <SearchBar
            height={40}
            value={search}
            onChange={setSearch}
            placeholder={tBilling('Advance.search_by_patient_name_mrn_or_mobile_no')}
            minWidth={420}
          />
        </Stack>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Content area for the active tab */}
      {renderTabContent()}

      {/* Shared filter modal */}
      <InvoiceFilter
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={(vals) => { setFilters(vals); setIsFilterOpen(false); }}
        showBillType={false}
        initialValues={filters}
      />
    </Box>
  );
};

export default Advance;
